import { includes, isEqual, isNumber } from 'lodash';

export const isRouteToDisplay = (feature, display) =>
  !display.showUnassigned &&
  (display.selectedTour !== undefined
    ? display.selectedTour === feature.properties.routeId
    : includes(display.routeIds, feature.properties.routeId));

export const isActivityToDisplay = (feature) => feature.properties.stopLocation;
export const isUndesiredPoint = (feature) =>
  feature.properties.isUndesired && feature.geometry.type === 'Point';
export const isUndesiredLine = (feature) =>
  feature.properties.isUndesiredLine && feature.geometry.type === 'LineString';

export const isOriginalLine = (feature) => feature.properties.isOriginalLocationLine;

export const isPointDepot = (feature) =>
  feature.properties.isDepot ||
  (!!feature.properties.jobIds && includes(feature.properties.jobIds, 'departure'));

export const isPointReturnLocation = (feature) =>
  feature.properties.isReturnLocation ||
  (!!feature.properties.jobIds && includes(feature.properties.jobIds, 'arrival'));

export const isUnassignedJob = ({ properties: { routeId, isUnassigned, isFleetStop } }) =>
  !isFleetStop && (!isNumber(routeId) || isUnassigned);

export const filterDepot = (mapData, isDepot) =>
  mapData.geo.features.filter((feature) =>
    isDepot
      ? isPointDepot(feature) === isDepot || isPointReturnLocation(feature) === isDepot
      : (isPointDepot(feature) === isDepot && isPointReturnLocation(feature) === isDepot) ||
        feature.properties.isUnassigned,
  );

export const filterActivities = (mapData, display) =>
  mapData.geo.features.filter(
    (feature) => isActivityToDisplay(feature) && isRouteToDisplay(feature, display),
  );

export const isLineToDisplay = (feature, routingData, showStraightRouteLines) =>
  feature.properties.isRouting || routingData?.requestError || showStraightRouteLines;

export const filterPoint = (feature, display) =>
  display.showProblem || isRouteToDisplay(feature, display) || isUnassignedJob(feature);

export const isPedestrianRoute = (feature) => feature.properties.mode === 'pedestrian';

export const isSegmentHidden = (feature, hiddenSegments) => {
  if (!hiddenSegments || hiddenSegments.length === 0) return false;
  const { routeId, fromStop } = feature.properties;
  return hiddenSegments.some((s) => s.routeId === routeId && s.fromStop === fromStop);
};

export const filterLineString = (feature, display, routingData, showStraightRouteLines) =>
  !display.showProblem &&
  !display.showUnassigned &&
  isRouteToDisplay(feature, display) &&
  isLineToDisplay(feature, routingData, showStraightRouteLines) &&
  !isSegmentHidden(feature, display.hiddenSegments);

export const filterHiddenSegment = (feature, display, routingData) =>
  !display.showProblem &&
  !display.showUnassigned &&
  feature.geometry.type === 'LineString' &&
  isRouteToDisplay(feature, display) &&
  isLineToDisplay(feature, routingData, false) &&
  !isPedestrianRoute(feature) &&
  !isUndesiredPoint(feature) &&
  !isUndesiredLine(feature) &&
  isSegmentHidden(feature, display.hiddenSegments);

// Selects all non-pedestrian routing segments (visible + hidden) for the transparent hit-area layer.
export const filterHitArea = (feature, display, routingData) =>
  !display.showProblem &&
  !display.showUnassigned &&
  feature.geometry.type === 'LineString' &&
  isRouteToDisplay(feature, display) &&
  isLineToDisplay(feature, routingData, false) &&
  !isPedestrianRoute(feature) &&
  !isUndesiredPoint(feature) &&
  !isUndesiredLine(feature);

// Pedestrian legs (e.g. parking to stop) are drawn as a thin dashed line, which makes them
// hard to hover precisely. Give them their own wide transparent hit-area layer, same as the
// driving segments above, instead of relying on the thin visible line for hover/click.
export const filterHitAreaPedestrian = (feature, display, routingData) =>
  !display.showProblem &&
  !display.showUnassigned &&
  feature.geometry.type === 'LineString' &&
  isRouteToDisplay(feature, display) &&
  isLineToDisplay(feature, routingData, false) &&
  isPedestrianRoute(feature) &&
  !isUndesiredPoint(feature) &&
  !isUndesiredLine(feature);

export const filterOriginalLocation = (feature, display) =>
  !display.showProblem &&
  !display.showUnassigned &&
  isOriginalLine(feature) &&
  isRouteToDisplay(feature, display);

export const featureFilter = (feature, display, routingData, showStraightRouteLines = false) =>
  feature.geometry.type === 'Point'
    ? filterPoint(feature, display)
    : filterLineString(feature, display, routingData, showStraightRouteLines);

export const featureFilterCluster = (feature, display, routingData, showStraightRouteLines) =>
  featureFilter(feature, display, routingData, showStraightRouteLines);

export const featureFilterUnselectedMarker = (feature, display, depot) => {
  if (display.showUnassigned) return isUnassignedJob(feature);
  return (
    (feature.geometry.type === 'Point' &&
      !isRouteToDisplay(feature, display) &&
      !isPointDepot(feature) &&
      !isPointReturnLocation(feature) &&
      !isActivityToDisplay(feature)) ||
    depot.some((d) => isEqual(d, feature.properties?.stopLocation)) ||
    isUnassignedJob(feature)
  );
};

export const featureFilterUnselectedTourLine = (feature, display) =>
  !isRouteToDisplay(feature, display);
